/**
 * Where the caret is on screen, for the deck editor's growing fields.
 *
 * `deckFocusPositioning.caretKeepDelta` needs one number while a field grows
 * under typing: the lowest point on screen that must stay inside the band for
 * the caret to remain visible. Until this module existed it used the FIELD's
 * own bottom as a stand-in, which is exact for a caret at the end of the text
 * and wrong everywhere else — a newline pressed in the middle of a long field
 * grows the field downward, and keeping its bottom in view scrolls the caret
 * off the top of the band, which is the defect this closes.
 *
 * A textarea gives up no caret geometry of its own: `Selection` and `Range`
 * do not reach inside a form control's shadow text, and `scrollTop` cannot be
 * used to infer it here because these fields are `overflow: hidden` and always
 * exactly their content's height (`GrowingField`), so their scrollTop is 0 by
 * construction. The measurement is therefore made in a MIRROR — a hidden div
 * carrying the field's own text metrics, holding the text up to the caret, with
 * a marker span whose `offsetTop` is the caret's line.
 *
 * What this module deliberately is NOT:
 *
 * - It is not a second scroll path. It reads geometry and returns a number;
 *   the caller feeds it to the same `caretKeepDelta` → `aim('caret')` →
 *   `createScrollMotion` chain that already existed. ADR-067 Decision D's one
 *   writer per focus gesture is untouched, and that is the whole point of
 *   returning a coordinate rather than doing anything with it.
 * - It shadows no scroll primitive and observes no subtree. Both techniques
 *   have hung this surface before (the rejected attempt `e4c8296`, named in
 *   ADR-067 §9.4) and are prohibited in this area. Nothing here patches a
 *   property or installs an observer of any kind.
 *
 * The mirror is a single reused node parked off screen on `document.body` —
 * outside the feed's scroller, absolutely positioned and hidden, so it can
 * neither affect the layout being measured nor be seen.
 */

/** Text metrics that decide where a line breaks and how tall it is. Copied from
 * the field so the mirror wraps its text at exactly the same points. */
const MIRRORED_STYLES = [
  'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
  'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontVariant',
  'letterSpacing', 'lineHeight', 'textIndent', 'textTransform',
  'wordSpacing', 'wordBreak', 'tabSize',
] as const

let mirrorNode: HTMLDivElement | null = null

function mirror(): HTMLDivElement {
  if (mirrorNode?.isConnected) return mirrorNode
  const el = document.createElement('div')
  el.setAttribute('aria-hidden', 'true')
  const s = el.style
  s.position = 'absolute'
  s.top = '0'
  // Off screen rather than `display: none`: a node with no box has no layout,
  // and layout is the entire measurement.
  s.left = '-9999px'
  s.visibility = 'hidden'
  s.pointerEvents = 'none'
  s.overflow = 'hidden'
  s.height = 'auto'
  document.body.appendChild(el)
  mirrorNode = el
  return el
}

/**
 * The bottom of the caret's own line, in viewport coordinates, plus whatever
 * the row keeps below its last line of text (the field's bottom padding and
 * border, and any part of the row sitting under the field).
 *
 * That trailing term is not decoration — it is what makes this a strict
 * refinement rather than a behaviour change. With the caret on the LAST line,
 * `caret line bottom + trailing chrome` is the row's bottom exactly, so the
 * number returned here equals the number the old rule used, and appending at
 * the end of a field behaves as it always did. The two only diverge when the
 * caret is somewhere above the last line — which is the case that was broken.
 *
 * Returns `null` when the caret cannot be located (no selection, a zero-height
 * field, an environment with no layout). The caller falls back to the row, so
 * an unmeasurable caret degrades to the previous behaviour rather than to no
 * behaviour.
 */
export function caretKeepBottom(row: HTMLElement, ta: HTMLTextAreaElement): number | null {
  if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return null

  const taRect = ta.getBoundingClientRect()
  if (taRect.height <= 0 || ta.offsetWidth <= 0) return null

  // For a backward selection the caret is at the START of the range; for a
  // collapsed one — every case that matters here, since this runs on growth
  // under typing — both ends are the same character.
  const caret = ta.selectionDirection === 'backward' ? ta.selectionStart : ta.selectionEnd
  if (caret == null) return null

  const cs = window.getComputedStyle(ta)
  const el = mirror()
  for (const prop of MIRRORED_STYLES) el.style[prop] = cs[prop]
  // Fixed rather than copied. `border-box` + the field's own outer width
  // reproduces its content width exactly whatever box model it is styled under,
  // and a textarea's wrapping is `pre-wrap` with long words broken — stating
  // both here means the mirror cannot inherit a page-level rule that would make
  // it break lines somewhere the field does not.
  el.style.boxSizing = 'border-box'
  el.style.width = `${ta.offsetWidth}px`
  el.style.whiteSpace = 'pre-wrap'
  el.style.overflowWrap = 'break-word'
  // Without a border STYLE the copied border widths compute to zero, which
  // would leave the mirror's content box two or three pixels wider than the
  // field's and break a line in a different place near the right edge. The
  // widths are copied above; this is what makes them take effect.
  el.style.borderStyle = 'solid'

  el.textContent = ta.value.slice(0, caret)
  const marker = document.createElement('span')
  // The remainder of the text, so the caret's line wraps exactly as the field's
  // does. A '.' when the caret is at the very end: an empty span has no box and
  // would report the wrong line.
  marker.textContent = ta.value.slice(caret) || '.'
  el.appendChild(marker)

  // `offsetTop` is measured from the offsetParent's padding edge — the mirror
  // is positioned, so it is the offsetParent, and the value already includes
  // its padding-top. Adding the field's top border converts it to a viewport y.
  const lineTop = marker.offsetTop
  const borderTop = parseFloat(cs.borderTopWidth) || 0
  const borderBottom = parseFloat(cs.borderBottomWidth) || 0
  const paddingBottom = parseFloat(cs.paddingBottom) || 0
  const lineHeight = parseFloat(cs.lineHeight) || (parseFloat(cs.fontSize) || 16) * 1.2

  // Emptied immediately: the node is reused across every measurement, and a
  // long field's text left parked in the DOM is laid out on every reflow the
  // page performs afterwards.
  el.textContent = ''

  const caretLineBottom = taRect.top + borderTop + lineTop + lineHeight
  const trailing = row.getBoundingClientRect().bottom - taRect.bottom + paddingBottom + borderBottom
  return caretLineBottom + trailing
}

/**
 * `caretKeepBottom` for the field that currently holds the caret inside `row`,
 * or `null` if that is not a textarea in this row.
 *
 * The focused element is resolved here rather than passed in because the
 * positioning effect holds a ROW, not a field: the row is what it observes for
 * growth and what it measures for placement, and which field inside it has the
 * caret is a fact about right now — read at the moment of the correction, which
 * is the moment it is true.
 */
export function focusedCaretKeepBottom(row: HTMLElement): number | null {
  const active = document.activeElement
  if (!(active instanceof HTMLTextAreaElement) || !row.contains(active)) return null
  return caretKeepBottom(row, active)
}
